(() => {
  // Prevent double-execution if the file is accidentally included twice
  if (window.__UNLTD_SUB_GIFTS_V3__) return;
  window.__UNLTD_SUB_GIFTS_V3__ = true;

  // ----------------------------
  // UNLTD - Subscription Gifts (Combined gift products)
  // Adds ONE gift line depending on 330 vs Pint subscription
  // ----------------------------

  // NEW combined gift variants
  const GIFT_330_VARIANT_ID  = 56569363923322; // 1/2 pint glass & bottle opener
  const GIFT_PINT_VARIANT_ID = 56569363956090; // pint glass & bottle opener

  // OLD gift variants (we will remove if they were previously added as gifts)
  const OLD_GIFT_VARIANT_IDS = new Set([
    56506062602618, // old half glass
    56506062569850, // old pint glass
    56333678543226, // old opener gold
    56333678575994  // old opener silver
  ]);

  // Subscription variants (ONLY count when item is a subscription)
  const SUB_330_VARIANTS = new Set([
    56507229536634, 56507229569402, 56507229602170,
    56507229438330, 56507229471098, 56507229503866,
    56507229831546, 56507229864314, 56507229897082,
    56507229733242, 56507229766010, 56507229798778,
    56507230126458, 56507230159226, 56507230191994,
    56507230028154, 56507230060922, 56507230093690
  ]);

  const SUB_568_VARIANTS = new Set([
    56507229634938, 56507229667706, 56507229700474,
    56507229929850, 56507229962618, 56507229995386,
    56507230224762, 56507230257530, 56507230290298
  ]);

  // Mark gifts with this property so we can reliably find/remove them
  const GIFT_PROP_KEY = "_unltd_gift";
  const GIFT_PROP_VAL = "subscription_freebie";

  let isSyncing = false;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function getCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to load cart");
    return res.json();
  }

  function isSubscriptionLine(item) {
    return !!item.selling_plan_allocation;
  }

  function hasGiftProperty(item) {
    return item.properties && item.properties[GIFT_PROP_KEY] === GIFT_PROP_VAL;
  }

  function cartNeeds(cart) {
    let needs330 = false;
    let needs568 = false;

    for (const item of cart.items) {
      if (!isSubscriptionLine(item)) continue;
      const vid = Number(item.variant_id);
      if (SUB_330_VARIANTS.has(vid)) needs330 = true;
      if (SUB_568_VARIANTS.has(vid)) needs568 = true;
    }

    // if both somehow exist, prioritise pint gift
    const desiredGift = needs568 ? GIFT_PINT_VARIANT_ID : (needs330 ? GIFT_330_VARIANT_ID : null);
    return { needs330, needs568, desiredGift };
  }

  function getGiftLines(cart) {
    // only lines we previously added (gift property)
    return cart.items.filter(hasGiftProperty);
  }

  async function setLineQuantity(lineKey, quantity) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineKey, quantity })
    });
    return res.ok;
  }

  async function addGift(variantId) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: { [GIFT_PROP_KEY]: GIFT_PROP_VAL }
        }]
      })
    });
    return res.ok;
  }

  async function syncSubscriptionGifts() {
    if (isSyncing) return;
    isSyncing = true;

    try {
      const cart = await getCart();
      const needs = cartNeeds(cart);

      // No subscription -> remove any gift lines we added
      if (!needs.desiredGift) {
        for (const li of getGiftLines(cart)) {
          await setLineQuantity(li.key, 0);
          await sleep(30);
        }
        return;
      }

      // Remove any WRONG gift lines we added (property-marked)
      for (const li of getGiftLines(cart)) {
        const vid = Number(li.variant_id);
        if (vid !== Number(needs.desiredGift)) {
          await setLineQuantity(li.key, 0);
          await sleep(30);
        } else if (li.quantity !== 1) {
          await setLineQuantity(li.key, 1);
          await sleep(30);
        }
      }

      // Remove OLD gift variants if they were previously added as gifts (property-marked)
      // (If a customer manually added a glass, it won't have the gift property so we leave it alone.)
      const cart2 = await getCart();
      for (const li of getGiftLines(cart2)) {
        if (OLD_GIFT_VARIANT_IDS.has(Number(li.variant_id))) {
          await setLineQuantity(li.key, 0);
          await sleep(30);
        }
      }

      // Ensure desired gift exists (property-marked)
      const cart3 = await getCart();
      const hasDesired = cart3.items.some(i => hasGiftProperty(i) && Number(i.variant_id) === Number(needs.desiredGift));
      if (!hasDesired) {
        await addGift(needs.desiredGift);
        await sleep(50);
      }
    } catch (e) {
      console.warn("[UNLTD gifts] Sync error", e);
    } finally {
      isSyncing = false;
    }
  }

  // Trigger points
  document.addEventListener("DOMContentLoaded", () => {
    syncSubscriptionGifts();
  });

  // Patch fetch so cart operations trigger sync (drawer/cart updates)
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const res = await originalFetch(...args);

    try {
      const url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url) || "";
      if (
        url.includes("/cart/add") ||
        url.includes("/cart/change") ||
        url.includes("/cart/update") ||
        url.includes("/cart/clear")
      ) {
        setTimeout(syncSubscriptionGifts, 250);
      }
    } catch (_) {}

    return res;
  };
})();
